import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllEvents } from '../../Redux/slices/eventSlice';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import HeroCarousel from '../Common/HeroCarousel';
import { FaCalendar, FaClock, FaMapMarkerAlt, FaArrowRight } from 'react-icons/fa';
import event1Image from '../../Assets/event 1.png';
import event2Image from '../../Assets/Event 2.png';
import event3Image from '../../Assets/Event 3.png';

const Event = () => {
  const dispatch = useDispatch();
  const { events, loading, error } = useSelector((state) => state.events);

  useEffect(() => {
    // Fetch all events (both upcoming and past)
    dispatch(getAllEvents());
  }, [dispatch]);

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    
    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', options);
    }
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', options)}`;
  };

  // Format time range
  const formatTimeRange = (startTime, endTime) => {
    if (endTime) {
      return `${startTime} - ${endTime}`;
    }
    return startTime;
  };

  // Get featured event (first event marked as featured or first upcoming event)
  const featuredEvent = events.find(event => event.isFeatured) || events[0];
  
  // Get all other events (exclude featured event)
  const upcomingEvents = events.filter(event => event._id !== featuredEvent?._id);

  // Fallback images
  const getEventImage = (imageUrl, index = 0) => {
    if (imageUrl) return imageUrl;
    const fallbackImages = [event1Image, event2Image, event3Image];
    return fallbackImages[index % fallbackImages.length];
  };

  // Get badge color based on category
  const getBadgeColor = (category) => {
    const colors = {
      'Service': 'bg-blue-100 text-blue-600',
      'Conference': 'bg-purple-100 text-purple-600',
      'Seminar': 'bg-green-100 text-green-600',
      'Outreach': 'bg-orange-100 text-orange-600',
      'Youth Event': 'bg-pink-100 text-pink-600',
      'Children Event': 'bg-yellow-100 text-yellow-600',
      'Prayer Meeting': 'bg-indigo-100 text-indigo-600',
      'Other': 'bg-gray-100 text-gray-600'
    };
    return colors[category] || 'bg-blue-100 text-blue-600';
  };

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <HeroCarousel 
        title="Events"
        subtitle="Join us for transformative experiences, powerful ministry, and community connection"
        height="h-96"
        contentPosition="bottom"
      />

      {/* Events Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          {/* Section Title */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-2">Events</h2>
            <p className="text-gray-600 font-sf-pro">Don't miss these special upcoming ministry opportunities</p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Loading events...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-12">
              <p className="text-red-600 text-lg">{error}</p>
            </div>
          )}

          {/* No Events */}
          {!loading && !error && events.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No upcoming events at this time. Check back soon!</p>
            </div>
          )}

          {/* Featured Event Card */}
          {!loading && !error && featuredEvent && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto">
                  <img 
                    src={getEventImage(featuredEvent.imageUrl, 0)} 
                    alt={featuredEvent.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <span className={`inline-block ${getBadgeColor(featuredEvent.category)} text-sm px-4 py-1 rounded-full font-medium mb-4 font-sf-pro`}>
                    {featuredEvent.category}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">{featuredEvent.title}</h3>
                  <p className="text-gray-600 mb-6 font-sf-pro leading-relaxed">
                    {featuredEvent.description}
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-gray-700">
                      <FaCalendar className="text-primary" />
                      <span className="font-sf-pro">{formatDateRange(featuredEvent.startDate, featuredEvent.endDate)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-700">
                      <FaClock className="text-primary" />
                      <span className="font-sf-pro">{formatTimeRange(featuredEvent.startTime, featuredEvent.endTime)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-700">
                      <FaMapMarkerAlt className="text-primary" />
                      <span className="font-sf-pro">{featuredEvent.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Events Grid */}
          {!loading && !error && upcomingEvents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {upcomingEvents.map((event, index) => (
                <div key={event._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <div className="relative h-48">
                    <img 
                      src={getEventImage(event.imageUrl, index + 1)} 
                      alt={event.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3">{event.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 font-sf-pro leading-relaxed line-clamp-3">
                      {event.description}
                    </p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-gray-700 text-sm">
                        <FaCalendar className="text-primary text-xs" />
                        <span className="font-sf-pro">{formatDateRange(event.startDate, event.endDate)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 text-sm">
                        <FaClock className="text-primary text-xs" />
                        <span className="font-sf-pro">{formatTimeRange(event.startTime, event.endTime)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-700 text-sm">
                        <FaMapMarkerAlt className="text-primary text-xs" />
                        <span className="font-sf-pro">{event.location}</span>
                      </div>
                    </div>
                    <Link 
                      to="/contact"
                      className="w-full bg-black text-white py-2 rounded-lg font-medium hover:bg-black transition font-sf-pro flex items-center justify-center gap-2 group"
                    >
                      Contact us
                      <FaArrowRight className="opacity-0 group-hover:opacity-100 transform group-hover:translate-x-1 transition-all duration-200" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Event;